import { QWIKBOX_STORAGE_BASE_URL } from 'src/app/constants/storage.constants';
import { Component, Input, OnInit, Output, EventEmitter, isDevMode } from '@angular/core';
import * as _ from 'lodash';
import { HubHighlightRecord } from 'src/app/models/hub';
import { ApiHelperService } from 'src/app/services/api-helper.service';
import { DialogService } from 'src/app/services/dialog.service';
import { HighlightsService } from 'src/app/services/highlights.service';


//Used for loosly tracking clicks on highlight reactions
export interface LocalStorageHighlightReaction {
  id: string;
  like: boolean;
  love: boolean;
  fire: boolean;
}

@Component({
  selector: 'app-highlight-card',
  templateUrl: './highlight-card.component.html',
  styleUrls: ['./highlight-card.component.css']
})
export class HighlightCardComponent implements OnInit {

  profileImage: string;
  lsReaction: LocalStorageHighlightReaction;
  primaryColor: string = "#2e3338";
  headerImageUrl: string | null = null;
  headerName: string = '';
  highlightTitle: string = '';
  highlightDate: Date | string = '';
  highlightViews: number = 0;
  thumbnailUrl: string = '';
  routerLink: any[] = ['/hub'];
  
  @Input() hideCloseButton: boolean = false;
  @Input() hideHeader: boolean = false;
  @Input() selected: boolean = false;  
  @Output() closedCard: EventEmitter<HubHighlightRecord> = new EventEmitter<HubHighlightRecord>();
  
  _highlightRecord: HubHighlightRecord;
  get highlightRecord(): HubHighlightRecord {
    return this._highlightRecord;
  }
  @Input() set highlightRecord(value: HubHighlightRecord) {
    this._highlightRecord = value;

    if (!this._highlightRecord?.highlight) {
      this.resetDisplayValues();
      return;
    }

    this.primaryColor = "#2e3338";
    this.getLSForHighlight();
    this.profileImage = this.getUserImage();
    this.headerImageUrl = this.getSelectedHeaderImage();
    this.headerName = this.getSelectedHeaderName();
    this.highlightTitle = this._highlightRecord.highlight.title || 'QwikCut Highlight';
    this.highlightDate = this._highlightRecord.highlight.publishDate ||  this._highlightRecord.highlight.createdAt || this._highlightRecord.highlight.updatedAt || '';
    this.highlightViews = this._highlightRecord.highlight.views || 0;
    this.thumbnailUrl = this.getThumbnailForHighlight();
    this.routerLink = this.getRouterLink();

    if(!this._highlightRecord.highlight.reactionCounts) {
      //Set default - for old highlights
      this._highlightRecord.highlight.reactionCounts = {
        like: 0, love: 0, fire: 0
      }
    }
  }

  constructor(private api: HighlightsService, private _apiHelper: ApiHelperService, private dialog: DialogService) { }

  ngOnInit() {
		this.updateViews();
  }

  clickedPlay() {    
    if (!this._highlightRecord?.highlight) return;
    this.dialog.openHighlightVideoDialog(this._highlightRecord);
  }

  clickedClose() {
    this.closedCard.emit(this.highlightRecord);
  }
 
  getDefaultText(): string {
    if (this.isOfficialUser()) { return 'QC'; }
    if (this.isTeamOnlyHighlight()) { return this.getTeamInitials(); }
    if (!this.highlightRecord?.user) { return ''; }
    return `${this.highlightRecord.user.firstname || ''}${this.highlightRecord.user.lastname || ''}`.slice(0, 2);
  }

  onHeaderImageError(event: Event) {
    const image = event.target as HTMLImageElement;
    const fallback = this.selected ? '/assets/images/qc88.png' : '/assets/images/portrait-placeholder.png';
    if (image.src.endsWith(fallback)) return;
    image.src = fallback;
  }

  resetDisplayValues() {
    this.profileImage = null;
    this.headerImageUrl = null;
    this.headerName = '';
    this.highlightTitle = '';
    this.highlightDate = '';
    this.highlightViews = 0;
    this.thumbnailUrl = '';
    this.routerLink = ['/hub'];
  }

  getUserImage(): string | null {
    
    if(this.isOfficialUser()) {
      this.primaryColor = "#507c23";
      return '/assets/images/qc88.png';
    }

    if (this.isTeamOnlyHighlight()) {
      return this.getTeamImage();
    }

    if(!this.highlightRecord.metric || !this.highlightRecord.metric.profile || !this.highlightRecord.metric.profile.picture ) {
      return null;
    }
    
    let parentFolder = 'social_media'
    if (isDevMode()) parentFolder = 'dev_social_media';
    return `${QWIKBOX_STORAGE_BASE_URL}/${parentFolder}/${this.highlightRecord.highlight.userid}/${this.highlightRecord.metric.profile.picture}`;
  }

  getHeaderImage(): string | null {
    return this.headerImageUrl;
  }

  getSelectedHeaderImage(): string {
    if (this.isOfficialUser()) {
      return '/assets/images/qc88.png';
    }

    if (this.highlightRecord.highlight?.titleInfo?.imageurl) {
      return this.highlightRecord.highlight.titleInfo.imageurl;
    }

    if (this.isTeamOnlyHighlight()) {
      return this.getTeamImage();
    }

    const profileImage = this.getAthleteMetricsProfileImage();
    return profileImage || '/assets/images/qc88.png';
  }

  getAthleteMetricsProfileImage(): string | null {
    if(!this.highlightRecord.highlight?.userid || !this.highlightRecord.metric?.profile?.picture) {
      return null;
    }

    let parentFolder = 'social_media'
    if (isDevMode()) parentFolder = 'dev_social_media';
    return `${QWIKBOX_STORAGE_BASE_URL}/${parentFolder}/${this.highlightRecord.highlight.userid}/${this.highlightRecord.metric.profile.picture}`;
  }

  getTeamImage(): string {
    const defaultImage = "/assets/images/team-logo-default.png";

    if(!this.highlightRecord || !this.highlightRecord.teamSettings || !this.highlightRecord.teamSettings.hub || !this.highlightRecord.teamSettings.hub.logo) { 
      return defaultImage;
    }

    const parentFolder = isDevMode() ? 'dev_social_media' : "social_media";     
    return `${QWIKBOX_STORAGE_BASE_URL}/${parentFolder}/team/${this.highlightRecord.team._id}/${this.highlightRecord.teamSettings.hub.logo}`;
  }

  getNameForTitle(): string {
    if(this.isOfficialUser()) {
      return 'Official QwikCut Highlight';
    }

    if (this.isTeamOnlyHighlight()) return this.getTeamNameForHighlight();

    if (!this.highlightRecord?.user) { return ''; }

    let nick = this.highlightRecord.metric?.nickName ? ' "' + this.highlightRecord.metric.nickName + '" ' : ' ';
    return this.highlightRecord.user.firstname + nick + this.highlightRecord.user.lastname;
  }

  getSelectedHeaderName(): string {
    if (this.isOfficialUser()) {
      return 'QwikCut';
    }

    const playerName = this.highlightRecord.highlight?.titleInfo?.playername;
    if (playerName) return playerName;

    if (this.isTeamOnlyHighlight()) return this.getTeamNameForHighlight();

    if (!this.highlightRecord.user) return '';
    let nick = this.highlightRecord.metric?.nickName ? ' "' + this.highlightRecord.metric.nickName + '" ' : ' ';
    return this.highlightRecord.user.firstname + nick + this.highlightRecord.user.lastname;
  }

  getViewsForTitle() {
    const views = this.highlightRecord.highlight?.views || 0;
    return `${views} View${views == 1 ? '' : 's'}`;
  }

  getThumbnailForHighlight(): string {
    if(!this.highlightRecord.highlight?.highlightlink) { return ''; }
    const highlightName = this.highlightRecord.highlight._id;
    return _.split(this.highlightRecord.highlight.highlightlink, highlightName)[0] + `thumbnails/${highlightName}.jpg`
  }

  downloadFile() {
    window.open(this.highlightRecord.highlight.highlightlink, '_blank');
  }

  getMailLinkForHighlight(): string {
    if(!this.highlightRecord.highlight) { return ''; }
    const url = this.getShareUrl();
    const encodedName = encodeURIComponent(`${this.highlightRecord.highlight.title} - Video Highlight - Powered by QwikCut`)
    return `mailto:?subject=${encodedName}&body=${url}`;
  }

  getShareUrl(): string {
    if(!this.highlightRecord.highlight) { return ""; }
    const subdomain = isDevMode() ? 'dev-share' : 'share';
    return `https://${subdomain}.qwikcut.com/${this.highlightRecord.highlight._id}/${encodeURIComponent(this.highlightRecord.highlight.title)}`;    
  }

  getTitle() {
    if (!this.highlightRecord.highlight) return 'QwikCut Highlight'
    return this.highlightRecord.highlight.title
  }

  updateViews() {
		if (!this.highlightRecord?.highlight || !this.selected) return;
    //At a future date we will want to do more than check if play is hit, but for now that is good. Approved by Chad 
    this.api.incrementHighlightViews(this.highlightRecord.highlight._id)
      .then(_ => {})
      .catch(error => console.error(error));
  }

  copyHighlightLinkToClipboard(_id) {    
    const text = this.getShareUrl();
    var clipBoardElem = document.createElement("input");
    
    document.body.appendChild(clipBoardElem);
    clipBoardElem.value = text;
    clipBoardElem.select();
    
    var successfulCopy = document.execCommand('copy');
    document.body.removeChild(clipBoardElem)
    
    if(successfulCopy) {
      this._apiHelper.showSuccessResponse("Copied to Clipboard");
    } else {
      this._apiHelper.showErrorResponse("There was a problem trying to copy this link.");
    }
  }

  shareToFacebook() {
    const url = this.getShareUrl();
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`,'facebook-share-dialog',"width=626, height=436");
  }

  getTwitterUrl() {
    const link = this.getShareUrl();
    const encodedText = encodeURIComponent(this.getTitle() + " - ");
    const encodedUrl = encodeURIComponent(link);
    const encodedHashtags = encodeURIComponent("QwikCut");
    return `https://twitter.com/intent/tweet?hashtags=${encodedHashtags}&text=${encodedText}&url=${encodedUrl}`;
  }

  getRouterLink() {
    if (this.isTeamOnlyHighlight()) {
      const teamid = this.highlightRecord?.highlight?.teamid || this.highlightRecord?.team?._id;
      if (!teamid) return ['/hub'];

      const teamName = this.getTeamNameForHighlight();
      return ['/hub/team', teamid, this.getStringForRouterLink(teamName || 'Team')];
    }
    
    if (!this.highlightRecord?.highlight?.userid || !this.highlightRecord?.user) return ['/hub'];
    const user = this.highlightRecord.user;    
    const uname = this.getStringForRouterLink(`${user.firstname}-${user.lastname}`);
    return ['/hub/detail', this.highlightRecord.highlight.userid, uname];
  }

  getStringForRouterLink(input: string): string {

    if(this.isOfficialUser()) {
      return 'QwikCut';
    }

    const regex = / /gm;
    return String(input).replace(regex, '-'); //Replace all spaces with dashes, dont' want %20's.
  }

  isOfficialUser(): boolean {
    const user = this.highlightRecord?.user as any;
    const role = user?.currentUserRole?.role;
    return !!user?.officalHighlight || _.includes(['admin', 'subadmin', 'junioradmin'], role);
  }

  isTeamOnlyHighlight(): boolean {
    return !!this.highlightRecord?.highlight && !this.highlightRecord.highlight.userid;
  }

  getTeamNameForHighlight(): string {
    return this.highlightRecord?.team?.name || this.highlightRecord?.highlight?.titleInfo?.playerteam || 'Team Highlight';
  }

  getTeamInitials(): string {
    const teamName = this.getTeamNameForHighlight();
    if (!teamName || teamName === 'Team Highlight') return 'TH';
    const words = _.filter(_.split(teamName, /\s+/), word => !!word);
    if (!words.length) return 'TH';
    return _.map(words.slice(0, 2), word => word.charAt(0)).join('');
  }

  updateReactions(type: 'like' | 'love' | 'fire', value: boolean) {
    /*
    Per Chad 1 July 2021
    - No decrementing reaction count, only adding
    - Do not require user to be logged in, to add a reaction
    - Do not care if someone is able to spam like's button to create fake reactions
    */

    //Increment only positive values
    if(value) {
      this.api.incrementHighlightReactions(this.highlightRecord.highlight._id, type)
        .then(_ => {})
        .catch(error => console.error(error))  
    }

    let count = this.highlightRecord.highlight.reactionCounts;

    //Update the local side counts and value immeditly without requiring a round trip to refresh
    //Also gives the appearnce of decrementing to balance between chad's requirement above and user fustration
    switch (type) {
      case 'fire':
        this.lsReaction.fire = value;
        count.fire = value ? count.fire + 1 : count.fire - 1;
        break;

      case 'like':
        this.lsReaction.like = value;
        count.like = value ? count.like + 1 : count.like - 1;
        break;

      case 'love':
        this.lsReaction.love = value;
        count.love = value ? count.love + 1 : count.love - 1;
        break;
    
      default:
        break;
    }

    this.saveLSForHighlight();

  }

  getLSForHighlight() {
    //Get's local storage reaction object for given highlight, stored by the highlights id
    let objJSON = localStorage.getItem(this.highlightRecord.highlight._id);

    //No highlight stored
    if(!objJSON || objJSON == "") {
      this.lsReaction = this.getDefaultLSValue();
      return;
    }

    try {
      this.lsReaction = JSON.parse(objJSON);
    } catch (error) {
      this.lsReaction = this.getDefaultLSValue(); //Get default on error (improperly stored object or empty string)
    }
  }

  saveLSForHighlight() {
    localStorage.setItem(this.highlightRecord.highlight._id, JSON.stringify(this.lsReaction));
  }

  getDefaultLSValue(): LocalStorageHighlightReaction {
    return {
      id: this.highlightRecord.highlight._id,
      like: false,
      love: false,
      fire: false
    };
  }

  getCountForReaction(type: string): string {
    let display = ""; //Default no display for unset or zero reactions
    let counts = this.highlightRecord.highlight.reactionCounts;

    //No reaction counts set yet
    if(!counts) {
      return display;
    }

    switch (type) {
      case 'like':
        return counts.like > 0 ? `${counts.like}` : "";
      case 'love':
        return counts.love > 0 ? `${counts.love}` : "";
      case 'fire':
        return counts.fire > 0 ? `${counts.fire}` : "";
    
      default:
        break;
    }
    return display;
  }

}
